/**
 * Context-sensitive action dropdown for a unified Role Master row.
 *
 * Adapts available actions based on the entry's link_status:
 *   - Always: View Details, Edit Role, Reassign Positions, Deprecate
 *   - Unlinked: Link to Suggestion, Search F&O Jobs, Create New Job
 *   - Linked: Edit F&O Job, Unlink
 *   - Mismatch: Update F&O Title, Accept Variant, Edit F&O Job, Unlink
 */

import React from 'react';
import {
  MoreHorizontal, Eye, Edit, ArrowRight, Trash2,
  Link2, Search, Plus, Settings, RefreshCw, Unlink, CheckCircle, ArrowDownToLine,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { UnifiedRoleMasterEntry } from '@/services/api/roleMasterService';

export interface RoleRowActionsProps {
  entry: UnifiedRoleMasterEntry;
  onViewDetails: (entry: UnifiedRoleMasterEntry) => void;
  onEdit: (entry: UnifiedRoleMasterEntry) => void;
  onReassignPositions: (entry: UnifiedRoleMasterEntry) => void;
  onDeprecate: (entry: UnifiedRoleMasterEntry) => void;
  onReactivate: (entry: UnifiedRoleMasterEntry) => void;
  // F&O actions
  onLinkSuggestion: (entry: UnifiedRoleMasterEntry) => void;
  onSearchFoJobs: (entry: UnifiedRoleMasterEntry) => void;
  onCreateFoJob: (entry: UnifiedRoleMasterEntry) => void;
  onEditFoJob: (entry: UnifiedRoleMasterEntry) => void;
  onUpdateFoTitle: (entry: UnifiedRoleMasterEntry) => void;
  onAcceptVariant: (entry: UnifiedRoleMasterEntry) => void;
  onAdoptFoGrade: (entry: UnifiedRoleMasterEntry) => void;
  onUnlink: (entry: UnifiedRoleMasterEntry) => void;
}

const RoleRowActions: React.FC<RoleRowActionsProps> = ({
  entry,
  onViewDetails,
  onEdit,
  onReassignPositions,
  onDeprecate,
  onReactivate,
  onLinkSuggestion,
  onSearchFoJobs,
  onCreateFoJob,
  onEditFoJob,
  onUpdateFoTitle,
  onAcceptVariant,
  onAdoptFoGrade,
  onUnlink,
}) => {
  const hasSuggestion = entry.suggestions && entry.suggestions.length > 0;
  const topSuggestion = hasSuggestion ? entry.suggestions[0] : null;
  const isLinked = entry.link_status === 'linked';
  const isMismatch = entry.link_status === 'mismatch';
  const isUnlinked = entry.link_status === 'unlinked';
  const hasPositions = entry.linked_positions_count > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => e.stopPropagation()}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" onClick={(e) => e.stopPropagation()}>
        {/* ── Always available ── */}
        <DropdownMenuItem onClick={() => onViewDetails(entry)}>
          <Eye className="h-4 w-4 mr-2" /> View Details
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onEdit(entry)}>
          <Edit className="h-4 w-4 mr-2" /> Edit Role
        </DropdownMenuItem>
        {hasPositions && (
          <DropdownMenuItem onClick={() => onReassignPositions(entry)}>
            <ArrowRight className="h-4 w-4 mr-2" /> Reassign Positions
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {/* ── Unlinked actions ── */}
        {isUnlinked && (
          <>
            {hasSuggestion && (
              <DropdownMenuItem onClick={() => onLinkSuggestion(entry)}>
                <Link2 className="h-4 w-4 mr-2" />
                Link ({Math.round((topSuggestion?.confidence || 0) * 100)}%)
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onSearchFoJobs(entry)}>
              <Search className="h-4 w-4 mr-2" /> Search F&O Jobs
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onCreateFoJob(entry)}>
              <Plus className="h-4 w-4 mr-2" /> Create New F&O Job
            </DropdownMenuItem>
          </>
        )}

        {/* ── Linked / Mismatch actions ── */}
        {(isLinked || isMismatch) && (
          <>
            <DropdownMenuItem onClick={() => onEditFoJob(entry)}>
              <Settings className="h-4 w-4 mr-2" /> Edit F&O Job
            </DropdownMenuItem>
            {isMismatch && (
              <>
                <DropdownMenuItem onClick={() => onUpdateFoTitle(entry)}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Update F&O Title
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAcceptVariant(entry)}>
                  <CheckCircle className="h-4 w-4 mr-2" /> Accept as Variant
                </DropdownMenuItem>
              </>
            )}
            {entry.grade_only_in_fo && (
              <DropdownMenuItem onClick={() => onAdoptFoGrade(entry)}>
                <ArrowDownToLine className="h-4 w-4 mr-2" /> Adopt F&O grade
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onUnlink(entry)} className="text-red-600">
              <Unlink className="h-4 w-4 mr-2" /> Unlink from F&O
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />

        {entry.status !== 'deprecated' ? (
          <DropdownMenuItem onClick={() => onDeprecate(entry)} className="text-red-600">
            <Trash2 className="h-4 w-4 mr-2" /> Deprecate
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={() => onReactivate(entry)} className="text-green-600">
            <CheckCircle className="h-4 w-4 mr-2" /> Reactivate
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RoleRowActions;
